<template>
  <v-dialog
    v-model="showDialog"
    max-width="700"
    transition="dialog-slide-transition"
    :fullscreen="$vuetify.breakpoint.xs"
    scrollable
    @input="handleDialogToggle"
  >
    <v-card class="enhanced-contact-dialog" elevation="24">
      <v-card-text class="dialog-content">
        <div class="dialog-header">
          <div class="header-content">
            <h2 class="dialog-title">Let's Create Something Amazing</h2>
            <p class="dialog-subtitle">
              I'm excited to hear about your project. Let's discuss how we can work together.
            </p>
          </div>
          <v-btn icon class="close-btn" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="header-divider"></v-divider>
          <!-- Contact Options -->
          <div class="contact-options">
            <div
              v-for="(social, index) in socialMedia"
              :key="index"
              class="contact-option"
              @click="openLink(social.url)"
            >
              <div class="option-icon" :style="{ backgroundColor: social.color + '20', color: social.color }">
                <v-icon>{{ social.icon }}</v-icon>
              </div>
              <div class="option-content">
                <h4 class="option-title">{{ social.name }}</h4>
                <p class="option-description">{{ social.description }}</p>
              </div>
              <v-icon color="grey" class="option-arrow">mdi-chevron-right</v-icon>
            </div>
          </div>

          <!-- Enhanced Contact Form -->
          <div class="enhanced-contact-form">
            <div class="form-header">
              <h3 class="form-title">Send me a message</h3>
              <p class="form-subtitle">I'll get back to you within 24 hours</p>
            </div>

            <v-form ref="contactForm" v-model="validContactForm" class="contact-form-container">
              <div class="form-grid">
                <div class="form-group">
                  <label class="form-label">
                    <v-icon size="18" color="primary">mdi-account</v-icon>
                    Your Name *
                  </label>
                  <v-text-field
                    v-model="contactForm.name"
                    :rules="nameRules"
                    placeholder="Enter your full name"
                    required
                    outlined
                    class="form-field modern-field"
                    hide-details="auto"
                  ></v-text-field>
                </div>

                <div class="form-group">
                  <label class="form-label">
                    <v-icon size="18" color="primary">mdi-email</v-icon>
                    Your Email *
                  </label>
                  <v-text-field
                    v-model="contactForm.email"
                    :rules="emailRules"
                    placeholder="your.email@example.com"
                    required
                    outlined
                    class="form-field modern-field"
                    hide-details="auto"
                  ></v-text-field>
                </div>

                <div class="form-group full-width">
                  <label class="form-label">
                    <v-icon size="18" color="primary">mdi-tag</v-icon>
                    Project Subject
                  </label>
                  <v-text-field
                    v-model="contactForm.subject"
                    placeholder="Brief description of your project"
                    outlined
                    class="form-field modern-field"
                    hide-details="auto"
                  ></v-text-field>
                </div>

                <div class="form-group full-width">
                  <label class="form-label">
                    <v-icon size="18" color="primary">mdi-message-text</v-icon>
                    Tell me about your project *
                  </label>
                  <v-textarea
                    v-model="contactForm.message"
                    :rules="messageRules"
                    placeholder="Describe your project, timeline, budget, and any specific requirements..."
                    required
                    outlined
                    auto-grow
                    rows="5"
                    class="form-field modern-field"
                    hide-details="auto"
                  ></v-textarea>
                </div>
              </div>

              <div class="form-actions">
                <v-btn
                  color="primary"
                  x-large
                  rounded
                  :disabled="!validContactForm"
                  :loading="isSubmitting"
                  @click="submitContactForm"
                  class="enhanced-submit-btn"
                  min-width="200"
                >
                  <v-icon left>mdi-rocket-launch</v-icon>
                  Send Message
                  <template v-slot:loader>
                    <v-icon class="rotating">mdi-loading</v-icon>
                  </template>
                </v-btn>

                <p class="form-note">
                  <v-icon size="16" color="success">mdi-shield-check</v-icon>
                  Your information is secure and will never be shared
                </p>
              </div>
            </v-form>
          </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ContactDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    socialMedia: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      showDialog: this.value,
      validContactForm: false,
      isSubmitting: false,
      contactForm: {
        name: '',
        email: '',
        subject: '',
        message: ''
      },
      nameRules: [
        v => !!v || 'Name is required',
        v => (v && v.length >= 2) || 'Name must be at least 2 characters',
      ],
      emailRules: [
        v => !!v || 'E-mail is required',
        v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
      ],
      messageRules: [
        v => !!v || 'Message is required',
        v => (v && v.length >= 10) || 'Message must be at least 10 characters',
      ],
    }
  },
  watch: {
    value(newVal) {
      this.showDialog = newVal;
    },
    showDialog(newVal) {
      if (!newVal) {
        this.$emit('input', false);
      }
    }
  },
  methods: {
    handleDialogToggle(value) {
      this.$emit('input', value);
    },
    closeDialog() {
      this.showDialog = false;
    },
    openLink(url) {
      window.open(url, '_blank');
    },
    async submitContactForm() {
      if (this.$refs.contactForm.validate()) {
        this.isSubmitting = true;

        try {
          // Simulate API call delay
          await new Promise(resolve => setTimeout(resolve, 2000));

          // In a real application, you would send this data to your backend
          console.log('Form submitted:', this.contactForm);

          // Emit success event
          this.$emit('form-submitted', {
            message: "Thank you! Your message has been sent successfully. I'll get back to you soon!",
            formData: { ...this.contactForm }
          });

          // Reset form
          this.$refs.contactForm.reset();

          // Close dialog after a delay
          setTimeout(() => {
            this.closeDialog();
          }, 1500);

        } catch (error) {
          // Handle error
          console.error('Error submitting form:', error);
          this.$emit('form-error', {
            message: "Sorry, there was an error sending your message. Please try again."
          });
        } finally {
          this.isSubmitting = false;
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

// Enhanced Contact Dialog
.enhanced-contact-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  background: var(--v-background-base);
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(67, 97, 238, 0.02));
  border-bottom: 1px solid rgba(67, 97, 238, 0.1);

  .header-content {
    flex: 1;

    .dialog-title {
      font-size: 1.75rem;
      font-weight: 800;
      color: var(--v-textPrimary-base);
      margin-bottom: 0.5rem;
      font-family: 'Poppins', sans-serif;
    }

    .dialog-subtitle {
      color: var(--v-textSecondary-base);
      font-size: 1rem;
      opacity: 0.9;
      line-height: 1.5;
      margin: 0;
    }
  }

  .close-btn {
    background: rgba(67, 97, 238, 0.1) !important;
    color: var(--v-textPrimary-base) !important;

    &:hover {
      background: rgba(67, 97, 238, 0.15) !important;
      transform: rotate(90deg);
    }
  }
}

.dialog-content {
  padding: 0;
}

.header-divider {
  margin: 2rem 0 !important;
}

// Contact Options Grid
.contact-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 0 2rem;
}

.contact-option {
  display: flex;
  align-items: center;
  padding: 1.25rem;
  border-radius: 16px;
  background: rgba(67, 97, 238, 0.02);
  border: 2px solid transparent;
  cursor: pointer;
  transition: all $transition-medium;

  &:hover {
    border-color: rgba(67, 97, 238, 0.2);
    background: rgba(67, 97, 238, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(67, 97, 238, 0.1);

    .option-arrow {
      transform: translateX(5px);
      color: var(--v-primary-base) !important;
    }
  }

  .option-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .option-content {
    flex: 1;

    .option-title {
      font-size: 1rem;
      font-weight: 700;
      color: var(--v-textPrimary-base);
      margin-bottom: 0.25rem;
    }

    .option-description {
      font-size: 0.9rem;
      color: var(--v-textSecondary-base);
      opacity: 0.8;
      margin: 0;
    }
  }

  .option-arrow {
    transition: all $transition-medium;
    margin-left: 1rem;
  }
}

.enhanced-contact-form {
  border-top: 1px solid rgba(67, 97, 238, 0.1);
  padding: 2rem;
}

.form-header {
  text-align: center;
  margin-bottom: 2rem;

  .form-title {
    font-size: 1.8rem;
    font-weight: 800;
    color: var(--v-textPrimary-base);
    margin-bottom: 0.5rem;
    font-family: 'Poppins', sans-serif;
  }

  .form-subtitle {
    color: var(--v-textSecondary-base);
    font-size: 1rem;
    opacity: 0.8;
    margin: 0;
  }
}

.contact-form-container {
  width: 100%;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem 2rem;
  margin-bottom: 2rem;

  @media (max-width: $breakpoint-sm) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.form-group {
  display: flex;
  flex-direction: column;

  &.full-width {
    grid-column: 1 / -1;
  }
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--v-textPrimary-base);
  margin-bottom: 0.75rem;
}

.modern-field {
  ::v-deep .v-input__control {
    .v-input__slot {
      border-radius: 12px;
      border-color: rgba(67, 97, 238, 0.15) !important;
      background: rgba(67, 97, 238, 0.02);
      transition: all $transition-medium;
      min-height: 56px;

      &:hover {
        border-color: rgba(67, 97, 238, 0.25) !important;
        background: rgba(67, 97, 238, 0.04);
      }

      .v-text-field__slot input,
      .v-textarea__slot textarea {
        padding: 12px 16px;
        font-size: 1rem;
        color: var(--v-textPrimary-base);

        &::placeholder {
          color: var(--v-textSecondary-base);
          opacity: 0.7;
        }
      }
    }

    &.v-input--is-focused .v-input__slot {
      border-color: var(--v-primary-base) !important;
      background: rgba(67, 97, 238, 0.05);
      box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.1);
    }
  }

  &.v-textarea {
    ::v-deep .v-input__control .v-input__slot {
      min-height: 120px;
      align-items: flex-start;
      padding-top: 8px;
    }
  }
}

.form-actions {
  text-align: center;
  padding: 1rem 0;

  .form-note {
    margin-top: 1rem;
    font-size: 0.85rem;
    color: var(--v-textSecondary-base);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    opacity: 0.8;
  }
}

.enhanced-submit-btn {
  height: 56px !important;
  font-weight: 700;
  font-size: 1rem;
  text-transform: none;
  background: linear-gradient(135deg, $primary, $accent) !important;
  transition: all $transition-medium;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
    transition: all 0.5s;
  }

  &:hover:not(:disabled):not(.v-btn--loading) {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(67, 97, 238, 0.4);

    &::before {
      left: 100%;
    }
  }

  &:disabled:not(.v-btn--loading) {
    opacity: 0.6;
  }

  &.v-btn--loading {
    opacity: 0.8;
  }

  .rotating {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Responsive Design
@media (max-width: $breakpoint-xs) {
  .dialog-header {
    padding: 1.5rem;

    .dialog-title {
      font-size: 1.5rem;
    }

    .dialog-subtitle {
      font-size: 0.9rem;
    }
  }

  .dialog-content {
    padding: 1.5rem;
  }

  .contact-options {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .contact-option {
    padding: 1rem;

    .option-icon {
      width: 40px;
      height: 40px;
      margin-right: 0.75rem;
    }

    .option-title {
      font-size: 0.95rem;
    }

    .option-description {
      font-size: 0.85rem;
    }
  }

  .form-header {
    .form-title {
      font-size: 1.5rem;
    }

    .form-subtitle {
      font-size: 0.9rem;
    }
  }
}
</style>